// Multi-host attribution helpers shared between the UserDashboard
// event cards (graphql path) and the public Events page (REST path).
//
// The two paths get hosts in different shapes:
//   - GraphQL: SocialEvent.hosts is already a normalized [String!]!
//     produced by the server-side resolver.
//   - REST: the raw socialEvents row carries host1 (string OR array)
//     and a legacy host2 string. Mirror the server resolver so the
//     two paths render the same way.

/**
 * Normalize host1 + host2 from a raw socialEvents row into a single
 * ordered, deduped array of host strings. Drops blanks and "none"
 * sentinels the way the server does. Safe to call with anything.
 */
export const normalizeHostsFromRow = (row) => {
  if (!row || typeof row !== "object") return [];
  const out = [];
  const seen = new Set();
  const push = (v) => {
    if (typeof v !== "string") return;
    const s = v.trim();
    if (!s) return;
    if (s.toLowerCase() === "none") return;
    // Legacy placeholder from before the multi-host picker landed.
    // Hide it so old rows don't display a meaningless numeric ID
    // to users as if it were a captain name.
    if (s === "1705612509309") return;
    const key = s.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    out.push(s);
  };
  if (Array.isArray(row.host1)) row.host1.forEach(push);
  else push(row.host1);
  push(row.host2);
  return out;
};

/**
 * Render a host array as a sentence: "Hosted by Justin", "Hosted by
 * Justin & Cent", "Hosted by Justin, Cent & Jason". Returns null on
 * an empty array so callers can short-circuit JSX without an empty
 * line. Pass `prefix: false` to get just the names.
 *
 * Skips entries that look like email addresses for display purposes
 * — emails go into the array when the "Me" tile is picked but we
 * don't want them surfaced verbatim to users.
 */
export const formatHostsSentence = (hosts, { prefix = true } = {}) => {
  if (!Array.isArray(hosts) || hosts.length === 0) return null;
  const cleaned = hosts.filter(
    (h) => typeof h === "string" && h.trim() && !/@/.test(h)
  );
  if (cleaned.length === 0) return null;
  let names;
  if (cleaned.length === 1) {
    names = cleaned[0];
  } else if (cleaned.length === 2) {
    names = `${cleaned[0]} & ${cleaned[1]}`;
  } else {
    const head = cleaned.slice(0, -1).join(", ");
    const tail = cleaned[cleaned.length - 1];
    names = `${head} & ${tail}`;
  }
  return prefix ? `Hosted by ${names}` : names;
};
